import { 
  collection, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter, 
  getDocs, 
  doc, 
  getDoc,
  Timestamp,
  QueryDocumentSnapshot,
  DocumentData,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { 
  ActivityLog, 
  ActivityType, 
  ActivityLevel, 
  ActivityStatus,
  ActivityLogFilter,
  ActivityLogSearchResult,
  ActivityLogStats
} from '../types/activityLog';

// الحصول على مرجع مجموعة سجل النشاط
const getActivityLogCollection = () => collection(db, 'activityLogs');

/**
 * تسجيل نشاط جديد
 */
export const logActivity = async (
  type: ActivityType,
  title: string,
  description: string,
  userId: string,
  userName: string,
  userRole: string,
  targetType: string,
  targetId: string,
  options: {
    level?: ActivityLevel;
    status?: ActivityStatus;
    targetName?: string;
    oldData?: any;
    newData?: any;
    changes?: string[];
    userBranch?: string;
    ipAddress?: string;
    userAgent?: string;
    sessionId?: string;
    metadata?: any;
  } = {}
): Promise<string> => {
  try {
    const activityLog: Omit<ActivityLog, 'id'> = {
      type,
      level: options.level || ActivityLevel.MEDIUM,
      status: options.status || ActivityStatus.SUCCESS,
      
      userId,
      userName,
      userRole,
      userBranch: options.userBranch,
      
      title,
      description,
      
      targetType,
      targetId,
      targetName: options.targetName,
      
      oldData: options.oldData,
      newData: options.newData,
      changes: options.changes,
      
      ipAddress: options.ipAddress,
      userAgent: options.userAgent,
      sessionId: options.sessionId,
      
      createdAt: Timestamp.now().toDate(),
      
      metadata: options.metadata
    };

    const docRef = await addDoc(getActivityLogCollection(), activityLog);
    console.log('Activity logged successfully:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error logging activity:', error);
    throw error;
  }
};

/**
 * البحث في سجل النشاط
 */
export const searchActivityLogs = async (
  filter: ActivityLogFilter = {}
): Promise<ActivityLogSearchResult> => {
  try {
    let q = query(getActivityLogCollection());

    // فلتر حسب المستخدم
    if (filter.userId) {
      q = query(q, where('userId', '==', filter.userId));
    }
    if (filter.userRole) {
      q = query(q, where('userRole', '==', filter.userRole));
    }
    if (filter.userBranch) {
      q = query(q, where('userBranch', '==', filter.userBranch));
    }

    // فلتر حسب نوع العملية
    if (filter.type) {
      q = query(q, where('type', '==', filter.type));
    }
    if (filter.types && filter.types.length > 0) {
      q = query(q, where('type', 'in', filter.types));
    }
    if (filter.level) {
      q = query(q, where('level', '==', filter.level));
    }
    if (filter.status) {
      q = query(q, where('status', '==', filter.status));
    }

    // فلتر حسب الكائن المتأثر
    if (filter.targetType) {
      q = query(q, where('targetType', '==', filter.targetType));
    }
    if (filter.targetId) {
      q = query(q, where('targetId', '==', filter.targetId));
    }

    // فلتر حسب التاريخ
    if (filter.startDate) {
      const startTimestamp = typeof filter.startDate === 'string' 
        ? Timestamp.fromDate(new Date(filter.startDate))
        : Timestamp.fromDate(filter.startDate);
      q = query(q, where('createdAt', '>=', startTimestamp));
    }
    if (filter.endDate) {
      const endTimestamp = typeof filter.endDate === 'string'
        ? Timestamp.fromDate(new Date(filter.endDate))
        : Timestamp.fromDate(filter.endDate);
      q = query(q, where('createdAt', '<=', endTimestamp));
    }

    // ترتيب النتائج
    const sortBy = filter.sortBy || 'createdAt';
    const sortOrder = filter.sortOrder || 'desc';
    q = query(q, orderBy(sortBy, sortOrder));

    // تحديد عدد النتائج
    const pageLimit = filter.limit || 50;
    q = query(q, limit(pageLimit));

    const querySnapshot = await getDocs(q);
    
    const activities: ActivityLog[] = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date()
    } as ActivityLog));

    // فلتر النص (يتم محلياً لأن Firestore لا يدعم البحث النصي المتقدم)
    let filteredActivities = activities;
    if (filter.searchText) {
      const searchText = filter.searchText.toLowerCase();
      filteredActivities = activities.filter(activity => 
        activity.title.toLowerCase().includes(searchText) ||
        activity.description.toLowerCase().includes(searchText) ||
        activity.userName.toLowerCase().includes(searchText) ||
        activity.targetName?.toLowerCase().includes(searchText)
      );
    }

    const totalCount = filteredActivities.length;
    const currentPage = filter.page || 1;
    const totalPages = Math.ceil(totalCount / pageLimit);

    return {
      activities: filteredActivities,
      totalCount,
      totalPages,
      currentPage,
      hasNextPage: currentPage < totalPages,
      hasPreviousPage: currentPage > 1
    };
  } catch (error) {
    console.error('Error searching activity logs:', error);
    throw error;
  }
};

/**
 * الحصول على سجل نشاط محدد
 */
export const getActivityLogById = async (id: string): Promise<ActivityLog | null> => {
  try {
    const docRef = doc(getActivityLogCollection(), id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data(),
        createdAt: docSnap.data().createdAt?.toDate() || new Date()
      } as ActivityLog;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting activity log:', error);
    throw error;
  }
};

/**
 * الحصول على إحصائيات سجل النشاط
 */
export const getActivityLogStats = async (
  filter: Omit<ActivityLogFilter, 'page' | 'limit' | 'sortBy' | 'sortOrder'> = {}
): Promise<ActivityLogStats> => {
  try {
    const searchResult = await searchActivityLogs({
      ...filter,
      limit: 10000 // للحصول على جميع النتائج للإحصائيات
    });

    const activities = searchResult.activities;
    
    // إحصائيات أساسية
    const totalActivities = activities.length;
    
    // إحصائيات حسب النوع
    const activitiesByType: Record<ActivityType, number> = {} as Record<ActivityType, number>;
    Object.values(ActivityType).forEach(type => {
      activitiesByType[type] = 0;
    });
    
    // إحصائيات حسب المستوى
    const activitiesByLevel: Record<ActivityLevel, number> = {} as Record<ActivityLevel, number>;
    Object.values(ActivityLevel).forEach(level => {
      activitiesByLevel[level] = 0;
    });
    
    // إحصائيات حسب الحالة
    const activitiesByStatus: Record<ActivityStatus, number> = {} as Record<ActivityStatus, number>;
    Object.values(ActivityStatus).forEach(status => {
      activitiesByStatus[status] = 0;
    });
    
    // إحصائيات حسب المستخدم
    const activitiesByUser: Record<string, number> = {};
    
    // إحصائيات حسب التاريخ
    const activitiesByDate: Record<string, number> = {};
    
    // معالجة البيانات
    activities.forEach(activity => {
      // حسب النوع
      activitiesByType[activity.type]++;
      
      // حسب المستوى
      activitiesByLevel[activity.level]++;
      
      // حسب الحالة
      activitiesByStatus[activity.status]++;
      
      // حسب المستخدم
      const userKey = `${activity.userId}:${activity.userName}`;
      activitiesByUser[userKey] = (activitiesByUser[userKey] || 0) + 1;
      
      // حسب التاريخ
      const dateKey = new Date(activity.createdAt).toISOString().split('T')[0];
      activitiesByDate[dateKey] = (activitiesByDate[dateKey] || 0) + 1;
    });
    
    // أكثر المستخدمين نشاطاً
    const mostActiveUsers = Object.entries(activitiesByUser)
      .map(([userKey, count]) => {
        const [userId, userName] = userKey.split(':');
        return { userId, userName, count };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
    
    // الأنشطة الحرجة الأخيرة
    const recentCriticalActivities = activities
      .filter(activity => activity.level === ActivityLevel.CRITICAL)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 10);

    return {
      totalActivities,
      activitiesByType,
      activitiesByLevel,
      activitiesByStatus,
      activitiesByUser,
      activitiesByDate,
      mostActiveUsers,
      recentCriticalActivities
    };
  } catch (error) {
    console.error('Error getting activity log stats:', error);
    throw error;
  }
};

/**
 * حذف السجلات القديمة (للصيانة)
 */
export const cleanupOldActivityLogs = async (retentionDays: number = 90): Promise<number> => {
  try {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - retentionDays);
    
    const q = query(
      getActivityLogCollection(),
      where('createdAt', '<', Timestamp.fromDate(cutoffDate))
    );
    
    const querySnapshot = await getDocs(q);
    const deletedCount = querySnapshot.size;
    
    // حذف المستندات (يجب تنفيذها في دفعات للمجموعات الكبيرة)
    const batch = writeBatch(db);
    querySnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    await batch.commit();
    
    console.log(`Cleaned up ${deletedCount} old activity logs`);
    return deletedCount;
  } catch (error) {
    console.error('Error cleaning up old activity logs:', error);
    throw error;
  }
};

// دوال مساعدة لتسجيل أنواع مختلفة من الأنشطة

/**
 * تسجيل عملية صيانة
 */
export const logMaintenanceActivity = async (
  type: ActivityType,
  maintenanceId: string,
  maintenanceName: string,
  userId: string,
  userName: string,
  userRole: string,
  description: string,
  options: {
    oldData?: any;
    newData?: any;
    changes?: string[];
    level?: ActivityLevel;
  } = {}
) => {
  return logActivity(
    type,
    `${description} - ${maintenanceName}`,
    description,
    userId,
    userName,
    userRole,
    'maintenance',
    maintenanceId,
    {
      ...options,
      targetName: maintenanceName,
      level: options.level || ActivityLevel.MEDIUM
    }
  );
};

/**
 * تسجيل عملية عميل
 */
export const logCustomerActivity = async (
  type: ActivityType,
  customerId: string,
  customerName: string,
  userId: string,
  userName: string,
  userRole: string,
  description: string,
  options: {
    oldData?: any;
    newData?: any;
    changes?: string[];
    level?: ActivityLevel;
  } = {}
) => {
  return logActivity(
    type,
    `${description} - ${customerName}`,
    description,
    userId,
    userName,
    userRole,
    'customer',
    customerId,
    {
      ...options,
      targetName: customerName,
      level: options.level || ActivityLevel.MEDIUM
    }
  );
};

/**
 * تسجيل عملية مستخدم
 */
export const logUserActivity = async (
  type: ActivityType,
  targetUserId: string,
  targetUserName: string,
  userId: string,
  userName: string,
  userRole: string,
  description: string,
  options: {
    oldData?: any;
    newData?: any;
    changes?: string[];
    level?: ActivityLevel;
  } = {}
) => {
  return logActivity(
    type,
    `${description} - ${targetUserName}`,
    description,
    userId,
    userName,
    userRole,
    'user',
    targetUserId,
    {
      ...options,
      targetName: targetUserName,
      level: options.level || ActivityLevel.HIGH
    }
  );
};
